import React, { useState, useEffect, useCallback } from "react";
import apiClient from "../api/client";
import { can } from "../utils/permissions";
import { useCurrentUser } from "../hooks/useCurrentUser";
import NumericInput from "./ui/NumericInput";

const FEE_LABELS = {
  ORIGINATION: "עמלת הקצאה",
  PREPAYMENT_PENALTY: "קנס פירעון מוקדם",
  UNUSED_COMMITMENT: "עמלת מסגרת לא משוכה",
  MANAGEMENT: "דמי ניהול",
  LEGAL: 'שכ"ט / הוצאות משפטיות',
  APPRAISAL: "הערכת שווי",
  ARRANGEMENT: "עמלת ארגון",
  COVENANT_WAIVER: "עמלת ויתור קובננט",
  EXTENSION: "עמלת הארכה",
  CUSTOM: "אחר",
};

const FREQUENCY_LABELS = {
  MONTHLY: "חודשי",
  QUARTERLY: "רבעוני",
  ANNUAL: "שנתי",
};

function formatNumber(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString("he-IL");
}

function formatDate(d) {
  if (!d) return "—";
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("he-IL");
}

const EMPTY_FORM = {
  feeType: "ORIGINATION",
  amount: "",
  currency: "ILS",
  chargedAt: new Date().toISOString().slice(0, 10),
  note: "",
  isRecurring: false,
  frequency: "MONTHLY",
  startDate: new Date().toISOString().slice(0, 10),
  endDate: "",
};

export default function LoanFeesTab({ loanId }) {
  const user = useCurrentUser();
  const canEdit = can(user, "loanDetail.edit");

  const [feeEvents, setFeeEvents] = useState([]);
  const [recurringFees, setRecurringFees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [editRecurring, setEditRecurring] = useState(null); // { id, amount, note, endDate, active }

  const loadData = useCallback(() => {
    if (!loanId) return;
    setLoading(true);
    Promise.all([
      apiClient.get(`/fees/loan/${loanId}`).then((r) => r.data).catch(() => []),
      apiClient.get(`/fees/recurring/loan/${loanId}`).then((r) => r.data).catch(() => []),
    ]).then(([events, recurring]) => {
      setFeeEvents(events);
      setRecurringFees(recurring);
    }).finally(() => setLoading(false));
  }, [loanId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  function openModal() {
    setForm(EMPTY_FORM);
    setFormError("");
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setForm(EMPTY_FORM);
    setFormError("");
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setFormError("");
    if (!form.feeType) return setFormError("יש לבחור סוג עמלה");
    if (!form.amount || Number(form.amount) <= 0) return setFormError("יש להזין סכום חיובי");

    setSubmitting(true);
    try {
      if (form.isRecurring) {
        if (!form.startDate) return setFormError("יש להזין תאריך התחלה");
        await apiClient.post(`/fees/recurring/loan/${loanId}`, {
          feeType: form.feeType,
          amount: Number(form.amount),
          currency: form.currency,
          frequency: form.frequency,
          startDate: form.startDate,
          endDate: form.endDate || undefined,
          note: form.note,
        });
      } else {
        await apiClient.post(`/fees/loan/${loanId}`, {
          feeType: form.feeType,
          amount: Number(form.amount),
          currency: form.currency,
          chargedAt: form.chargedAt,
          note: form.note,
        });
      }
      closeModal();
      loadData();
    } catch (err) {
      setFormError(err.response?.data?.error || err.message || "שגיאה בשמירת העמלה");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDeleteFeeEvent(id) {
    if (!window.confirm("למחוק עמלה זו?")) return;
    try {
      await apiClient.delete(`/fees/${id}`);
      loadData();
    } catch (err) {
      alert(err.response?.data?.error || "שגיאה במחיקה");
    }
  }

  async function handleDeleteRecurring(id) {
    if (!window.confirm("למחוק לוח חיוב זה?")) return;
    try {
      await apiClient.delete(`/fees/recurring/${id}`);
      loadData();
    } catch (err) {
      alert(err.response?.data?.error || "שגיאה במחיקה");
    }
  }

  function openEditRecurring(rec) {
    setEditRecurring({
      id: rec._id,
      amount: rec.amount,
      note: rec.note || "",
      endDate: rec.endDate ? new Date(rec.endDate).toISOString().slice(0, 10) : "",
      active: rec.active,
    });
    setFormError("");
  }

  async function handleSaveRecurring(e) {
    e.preventDefault();
    setFormError("");
    setSubmitting(true);
    try {
      await apiClient.put(`/fees/recurring/${editRecurring.id}`, {
        amount: Number(editRecurring.amount),
        note: editRecurring.note,
        endDate: editRecurring.endDate || undefined,
        active: editRecurring.active,
      });
      setEditRecurring(null);
      loadData();
    } catch (err) {
      setFormError(err.response?.data?.error || "שגיאה בעדכון");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return <div className="info-value muted" style={{ padding: "var(--space-lg)" }}>טוען עמלות...</div>;
  }

  return (
    <div style={{ padding: "var(--space-md) 0" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--space-lg)" }}>
        <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 600 }}>היסטוריית עמלות</h3>
        {canEdit && (
          <button className="btn btn-primary" onClick={openModal} style={{ fontSize: "13px" }}>
            הוסף עמלה
          </button>
        )}
      </div>

      {/* Two sections */}
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-lg)" }}>

        {/* Fee Events Table */}
        <div>
          <h4 style={{ margin: "0 0 var(--space-sm) 0", fontSize: "0.875rem", color: "var(--text-secondary)", fontWeight: 600 }}>
            חיובים ({feeEvents.length})
          </h4>
          {feeEvents.length === 0 ? (
            <div className="info-value muted">אין חיובי עמלות</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid #f0f0f0" }}>
                    <th style={thStyle}>תאריך</th>
                    <th style={thStyle}>סוג עמלה</th>
                    <th style={{ ...thStyle, textAlign: "left" }}>סכום</th>
                    <th style={thStyle}>מטבע</th>
                    <th style={thStyle}>מקור</th>
                    <th style={thStyle}>הערה</th>
                    {canEdit && <th style={thStyle}></th>}
                  </tr>
                </thead>
                <tbody>
                  {feeEvents.map((evt) => (
                    <tr key={evt._id} style={{ borderBottom: "1px solid #f5f5f5" }}>
                      <td style={tdStyle}>{formatDate(evt.chargedAt)}</td>
                      <td style={tdStyle}>{FEE_LABELS[evt.feeType] || evt.feeType}</td>
                      <td style={{ ...tdStyle, textAlign: "left", fontVariantNumeric: "tabular-nums" }}>
                        {formatNumber(evt.amount)}
                      </td>
                      <td style={tdStyle}>{evt.currency}</td>
                      <td style={tdStyle}>
                        {evt.source === "AUTO" ? (
                          <span style={badgeStyle("#6b7280")}>אוטומטי</span>
                        ) : (
                          <span style={badgeStyle("var(--accent)")}>ידני</span>
                        )}
                      </td>
                      <td style={{ ...tdStyle, color: "var(--text-secondary)", maxWidth: 200 }}>
                        {evt.note || "—"}
                      </td>
                      {canEdit && (
                        <td style={tdStyle}>
                          <button
                            className="btn btn-ghost"
                            style={{ fontSize: "11px", padding: "2px 8px" }}
                            onClick={() => handleDeleteFeeEvent(evt._id)}
                          >
                            מחק
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Recurring Fees */}
        <div>
          <h4 style={{ margin: "0 0 var(--space-sm) 0", fontSize: "0.875rem", color: "var(--text-secondary)", fontWeight: 600 }}>
            חיובים חוזרים ({recurringFees.length})
          </h4>
          {recurringFees.length === 0 ? (
            <div className="info-value muted">אין חיובים חוזרים מוגדרים</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {recurringFees.map((rec) => (
                <div
                  key={rec._id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "10px 14px",
                    background: "#fafafa",
                    borderRadius: 8,
                    border: "1px solid #f0f0f0",
                    gap: 12,
                    opacity: rec.active ? 1 : 0.5,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: "13px" }}>
                      {FEE_LABELS[rec.feeType] || rec.feeType}
                      {!rec.active && (
                        <span style={{ ...badgeStyle("#6b7280"), marginRight: 8, fontSize: "11px" }}>לא פעיל</span>
                      )}
                    </div>
                    <div style={{ fontSize: "12px", color: "var(--text-secondary)", marginTop: 2 }}>
                      {formatNumber(rec.amount)} {rec.currency} · {FREQUENCY_LABELS[rec.frequency] || rec.frequency}
                      {" · "}תאריך הבא: {formatDate(rec.nextDueDate)}
                      {rec.endDate && ` · עד: ${formatDate(rec.endDate)}`}
                    </div>
                    {rec.note && (
                      <div style={{ fontSize: "12px", color: "var(--text-secondary)", marginTop: 2 }}>{rec.note}</div>
                    )}
                  </div>
                  {canEdit && (
                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                      <button
                        className="btn btn-ghost"
                        style={{ fontSize: "11px", padding: "2px 8px" }}
                        onClick={() => openEditRecurring(rec)}
                      >
                        עריכה
                      </button>
                      <button
                        className="btn btn-ghost"
                        style={{ fontSize: "11px", padding: "2px 8px" }}
                        onClick={() => handleDeleteRecurring(rec._id)}
                      >
                        מחק
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Fee Modal */}
      {showModal && (
        <div style={overlayStyle} onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div style={modalStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--space-lg)" }}>
              <h3 style={{ margin: 0 }}>הוסף עמלה</h3>
              <button className="btn btn-ghost" onClick={closeModal} style={{ fontSize: "18px", lineHeight: 1 }}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="field">
                  <label className="label">סוג עמלה</label>
                  <select
                    className="select"
                    value={form.feeType}
                    onChange={(e) => setForm((f) => ({ ...f, feeType: e.target.value }))}
                  >
                    {Object.entries(FEE_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>

                <div className="field">
                  <label className="label">סכום</label>
                  <NumericInput
                    className="input"
                    min="0"
                    value={form.amount}
                    onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                    placeholder="0.00"
                  />
                </div>

                <div className="field">
                  <label className="label">מטבע</label>
                  <select
                    className="select"
                    value={form.currency}
                    onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
                  >
                    <option value="ILS">ILS ₪</option>
                    <option value="USD">USD $</option>
                    <option value="EUR">EUR €</option>
                  </select>
                </div>

                <div className="field" style={{ gridColumn: "1 / -1" }}>
                  <label className="label" style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={form.isRecurring}
                      onChange={(e) => setForm((f) => ({ ...f, isRecurring: e.target.checked }))}
                    />
                    חיוב חוזר (במקום חד-פעמי)
                  </label>
                </div>

                {!form.isRecurring && (
                  <div className="field">
                    <label className="label">תאריך חיוב</label>
                    <input
                      className="input"
                      type="date"
                      value={form.chargedAt}
                      onChange={(e) => setForm((f) => ({ ...f, chargedAt: e.target.value }))}
                    />
                  </div>
                )}

                {form.isRecurring && (
                  <>
                    <div className="field">
                      <label className="label">תדירות</label>
                      <select
                        className="select"
                        value={form.frequency}
                        onChange={(e) => setForm((f) => ({ ...f, frequency: e.target.value }))}
                      >
                        {Object.entries(FREQUENCY_LABELS).map(([k, v]) => (
                          <option key={k} value={k}>{v}</option>
                        ))}
                      </select>
                    </div>
                    <div className="field">
                      <label className="label">תאריך התחלה</label>
                      <input
                        className="input"
                        type="date"
                        value={form.startDate}
                        onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))}
                      />
                    </div>
                    <div className="field">
                      <label className="label">תאריך סיום (אופציונלי)</label>
                      <input
                        className="input"
                        type="date"
                        value={form.endDate}
                        onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
                      />
                    </div>
                  </>
                )}

                <div className="field" style={{ gridColumn: "1 / -1" }}>
                  <label className="label">הערה</label>
                  <textarea
                    className="input"
                    rows={2}
                    value={form.note}
                    onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                    placeholder="הערה אופציונלית..."
                    style={{ resize: "vertical" }}
                  />
                </div>
              </div>

              {formError && (
                <p style={{ color: "var(--error, #dc2626)", fontSize: "13px", margin: "var(--space-sm) 0 0 0" }}>
                  {formError}
                </p>
              )}

              <div style={{ display: "flex", gap: "var(--space-sm)", justifyContent: "flex-end", marginTop: "var(--space-lg)" }}>
                <button type="button" className="btn btn-ghost" onClick={closeModal}>ביטול</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? "שומר..." : "שמור עמלה"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Recurring Modal */}
      {editRecurring && (
        <div style={overlayStyle} onClick={(e) => { if (e.target === e.currentTarget) setEditRecurring(null); }}>
          <div style={modalStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--space-lg)" }}>
              <h3 style={{ margin: 0 }}>עריכת חיוב חוזר</h3>
              <button className="btn btn-ghost" onClick={() => setEditRecurring(null)} style={{ fontSize: "18px", lineHeight: 1 }}>×</button>
            </div>
            <form onSubmit={handleSaveRecurring}>
              <div className="form-grid">
                <div className="field">
                  <label className="label">סכום</label>
                  <NumericInput
                    className="input"
                    min="0"
                    value={editRecurring.amount}
                    onChange={(e) => setEditRecurring((r) => ({ ...r, amount: e.target.value }))}
                  />
                </div>
                <div className="field">
                  <label className="label">תאריך סיום</label>
                  <input
                    className="input"
                    type="date"
                    value={editRecurring.endDate}
                    onChange={(e) => setEditRecurring((r) => ({ ...r, endDate: e.target.value }))}
                  />
                </div>
                <div className="field" style={{ gridColumn: "1 / -1" }}>
                  <label className="label">הערה</label>
                  <textarea
                    className="input"
                    rows={2}
                    value={editRecurring.note}
                    onChange={(e) => setEditRecurring((r) => ({ ...r, note: e.target.value }))}
                    style={{ resize: "vertical" }}
                  />
                </div>
                <div className="field" style={{ gridColumn: "1 / -1" }}>
                  <label className="label" style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={editRecurring.active}
                      onChange={(e) => setEditRecurring((r) => ({ ...r, active: e.target.checked }))}
                    />
                    פעיל
                  </label>
                </div>
              </div>

              {formError && (
                <p style={{ color: "var(--error, #dc2626)", fontSize: "13px", margin: "var(--space-sm) 0 0 0" }}>
                  {formError}
                </p>
              )}

              <div style={{ display: "flex", gap: "var(--space-sm)", justifyContent: "flex-end", marginTop: "var(--space-lg)" }}>
                <button type="button" className="btn btn-ghost" onClick={() => setEditRecurring(null)}>ביטול</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? "שומר..." : "עדכן"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Styles ────────────────────────────────────────────────────────────────

const thStyle = {
  padding: "8px 10px",
  textAlign: "right",
  fontSize: "11px",
  fontWeight: 600,
  color: "var(--text-secondary)",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
};

const tdStyle = {
  padding: "10px 10px",
  textAlign: "right",
  verticalAlign: "middle",
};

function badgeStyle(color) {
  return {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 12,
    fontSize: "11px",
    fontWeight: 600,
    background: color === "var(--accent)" ? "rgba(99,91,255,0.12)" : "#f3f4f6",
    color: color === "var(--accent)" ? "var(--accent)" : "#6b7280",
  };
}

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.4)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
  padding: "var(--space-md)",
};

const modalStyle = {
  background: "var(--bg-card, #fff)",
  borderRadius: 12,
  padding: "var(--space-lg)",
  width: "100%",
  maxWidth: 500,
  maxHeight: "90vh",
  overflowY: "auto",
  boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
};
